<template>
  <q-tr
    v-if="!mobile"
    :props="props"
    class="table-mode cmp-domain-result-item"
    :class="{
      'ws-dark': dark || isTheme(Theme.LOOPIA),
      'ws-highlighted': highlighted,
      occupied: domain && !domain.availableForPurchase,
      'is-loading': isLoading,
      'for-sale': forSale,
      error: hasAnyError,
    }"
    @click="buy"
  >
    <domain-title
      v-if="domain"
      class="td-domain"
      :props="props"
      :in-cart="isInCart"
    />
    <bundle-title
      v-else-if="bundle"
      class="td-domain"
      :dark="dark"
      :highlighted="highlighted"
      :props="props"
    />

    <template v-if="hasError">
      <q-td key="period" :props="props" class="td-period">
        <small class="table-item-status text-ws-error">
          <strong>{{ $t('domain_checker.error') }}</strong>
        </small>
      </q-td>

      <!-- PRICE cell colspanned to 2 cells-->
      <q-td
        key="price"
        :props="props"
        class="td-price text-right text-ws-error q-pr-md"
        colspan="2"
      >
        <small class="q-pr-md">{{ $t(error) }}</small>
      </q-td>
    </template>
    <template v-else>
      <template v-if="bundle || domain.availableForPurchase || isInCart">
        <!-- PERIOD cell -->
        <q-td key="period" :props="props" class="td-period ws-text-primary">
          <ws-period
            v-if="!isTransferInCart || bundle"
            :period="bundle ? bundle.mainDomain.period : domain.period"
          />
        </q-td>

        <!-- PRICE cell -->
        <q-td key="price" :props="props" class="td-price text-right">
          <price-cell
            :price="price"
            :period="period"
            :prolong-period="prolongPeriod"
            :transfer="isTransferInCart"
          />
        </q-td>
      </template>

      <!-- CTAs -->
      <q-td
        key="action"
        class="td-action"
        :props="props"
        :colspan="bundle || domain.availableForPurchase || isInCart ? 1 : 3"
      >
        <ctas
          ref="ctas"
          :transfer-in-cart="domain ? isTransferInCart : false"
          :in-cart="isInCart"
          :loading="isLoading"
          :domain="domain"
          :bundle="bundle"
          :path="path"
          :error="hasAnyError"
        />
      </q-td>
    </template>
  </q-tr>

  <!-- MOBILE VIEW -->
  <!-- The buy functionality is also available by clicking the button inside this div,
        so it is okay that this is not a button. -->
  <!-- eslint-disable
       vuejs-accessibility/click-events-have-key-events
       vuejs-accessibility/no-static-element-interactions
  -->
  <div
    v-else
    class="mobile cmp-domain-result-item"
    :class="{
      'ws-dark': dark || isTheme(Theme.LOOPIA),
      'ws-highlighted': highlighted,
      occupied: domain && !domain.availableForPurchase,
      'is-loading': isLoading,
      'for-sale': forSale,
      error: hasAnyError,
    }"
    @click="buy"
  >
    <!-- eslint-enable
     vuejs-accessibility/click-events-have-key-events
     vuejs-accessibility/no-static-element-interactions
    -->
    <div class="domain-info-col">
      <!-- DOMAIN -->
      <template v-if="domain">
        <domain-title mobile :props="props" :in-cart="isInCart" />

        <div
          v-if="!domain.availableForPurchase"
          key="period"
          :props="props"
          class="grid-item-status text-uppercase"
          :class="{ 'text-ws-error': hasError }"
        >
          {{
            hasError
              ? $t('domain_checker.error')
              : $t('domain_checker.occupied.short_status')
          }}
        </div>

        <!-- PRICE cell -->
        <div v-else>
          <div
            v-if="!hasError"
            key="price"
            :props="props"
            class="grid-item-price"
          >
            <price-cell
              :price="price"
              :period="period"
              :prolong-period="prolongPeriod"
              :transfer="isTransferInCart"
            />
          </div>
          <div v-else key="price" :props="props" class="grid-item-price">
            {{ $t(error) }}
          </div>
        </div>
      </template>

      <!-- BUNDLE -->
      <template v-else-if="bundle">
        <div class="domain-info-col">
          <bundle-title mobile :props="props" />

          <!-- PRICE cell -->
          <div key="price" :props="props" class="grid-item-price">
            <price-cell
              :price="price"
              :period="period"
              :prolong-period="prolongPeriod"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- CTAs -->
    <div key="action" class="item-ctas flex items-center no-wrap">
      <ws-message type="alert" inline class="q-mx-md" :path="path">
        {{
          hasError ? $t(error) : ''
        }}
      </ws-message>
      <ctas
        v-if="!hasError"
        ref="ctas"
        mobile
        :transfer-in-cart="domain ? isTransferInCart : false"
        :in-cart="isInCart"
        :loading="isLoading"
        :domain="domain"
        :bundle="bundle"
        :path="path"
        :error="hasAnyError"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from 'vue-property-decorator';

// Components
import {QTd, QTr} from 'quasar';

import WsMessage from '@WS_Components/WsMessage.vue';
import WsPeriod from '@WS_Components/WsPeriod.vue';
import PriceCell from '@/components/domain-checker/resultsTable/PriceCell.vue';
import Ctas from '@/components/domain-checker/resultsTable/Ctas.vue';
import BundleTitle from '@/components/domain-checker/resultsTable/BundleTitle.vue';
import DomainTitle from '@/components/domain-checker/resultsTable/DomainTitle.vue';
import type {IsDomainLoadingGetter} from '@/store/getters.d';
import { Theme } from '@loopia-group/services';

import {rmTld} from '@/utilities/filters';

import {mapGetters} from 'vuex';

import type {Domain, DomainBundle, Price} from '@/components/domain-checker/dc.types';
import {useCartItemsStore} from '@/store/cartItemsStore';

@Component({
  components: {
    QTr,
    QTd,
    DomainTitle,
    BundleTitle,
    Ctas,
    PriceCell,
    WsMessage,
    WsPeriod,
  },

  filters: { rmTld },

  computed: {
    ...mapGetters(['cartDomains', 'cartTransferDomains', 'isDomainLoading']),
  },
})
export default class DomainResultItem extends Vue {
  @Prop({ default: () => ({}) }) props!: any;
  @Prop(Boolean) readonly dark!: boolean;
  @Prop(Boolean) isBundle!: boolean;

  // store getters
  readonly cartDomains!: string[];
  readonly cartTransferDomains!: string[];
  isDomainLoading!: IsDomainLoadingGetter;
  Theme = Theme;

  get apiError(): boolean {
    return this.$messageService.hasMessages(this.path);
  }

  get isLoading() {
    return this.bundle
      ? this.isDomainLoading(this.bundle.mainDomain) ||
          this.isDomainLoading(this.bundle.discountedDomain)
      : this.isDomainLoading(this.domain);
  }

  get error() {
    return this.bundle
      ? this.bundle.mainDomain.error || this.bundle.discountedDomain.error
      : this.domain?.error;
  }

  get hasError() {
    return !!this.error;
  }

  get hasAnyError() {
    return this.hasError || this.apiError;
  }

  get path() {
    if (this.domain) {
      return `domainChecker.${this.domain.name}`;
    } else if (this.bundle) {
      return `domainChecker.${this.bundle.mainDomain.name}.${this.bundle.mainDomain.name}`;
    }
    return 'domainChecker.general';
  }

  get period() {
    return this.bundle ? this.bundle.mainDomain.period : this.domain?.period;
  }

  get prolongPeriod() {
    return this.bundle
      ? this.bundle.mainDomain.prolongPeriod
      : this.domain?.prolongPeriod;
  }

  get price(): Price | null {
    if (this.domain) {
      return this.domain.price || null;
    }
    // else is bundle

    const primaryPrice = this.bundle?.mainDomain?.price;
    const secondaryPrice = this.bundle?.discountedDomain?.price;

    if (!primaryPrice || !secondaryPrice) {
      return null;
    }

    const primaryMarketingPrice = primaryPrice.marketingPrice || 0;
    const secondaryMarketingPrice = secondaryPrice.marketingPrice || 0;
    const primaryActualPrice = primaryPrice.actualPrice || 0;
    const secondaryActualPrice = secondaryPrice.actualPrice || 0;

    return {
      actualPrice: primaryActualPrice + secondaryActualPrice,
      marketingPrice: primaryMarketingPrice + secondaryMarketingPrice || null,
      transferPrice: null, // not needed for bundle
      prolongPrice: null, // not needed for bundle
    } as Price;
  }

  get domain(): Domain | undefined {
    return !this.isBundle ? this.props.row?.domain : undefined;
  }

  get bundle(): DomainBundle | undefined {
    if (!this.isBundle) {
      return undefined;
    }

    if (this.bundleAvailable) {
      return this.props.row?.bundle;
    } else {
      return undefined;
    }
  }

  get bundleAvailable() {
    const bundle = this.props.row?.bundle;
    return (
      bundle &&
      bundle.mainDomain.availableForPurchase &&
      bundle.discountedDomain.availableForPurchase
    );
  }

  get isInCart() {
    return this.domain
      ? this.cartDomains.includes(this.domain.name)
      : this.cartDomains.includes(this.bundle?.mainDomain?.name || '') &&
          this.cartDomains.includes(this.bundle?.discountedDomain?.name || '');
  }

  get highlighted() {
    return this.isInCart && !this.apiError;
  }

  get isTransferInCart() {
    const itemStore = useCartItemsStore();
    return !!itemStore.cartTransferDomains?.includes(this.domain?.name || '');
  }

  computePercentageDiscountForDomain(
    domain: Domain,
    domain2?: Domain
  ): number | null {
    const domainPrice: Price | null = domain?.price || null;

    if (!domainPrice || !domain.availableForPurchase) {
      return null;
    }

    const { marketingPrice, actualPrice } = domainPrice;
    const marketingPrice2 = domain2?.price?.marketingPrice || 0;
    const actualPrice2 = domain2?.price?.actualPrice || 0;

    if (marketingPrice && actualPrice && actualPrice < marketingPrice) {
      return (
        ((marketingPrice + marketingPrice2 - (actualPrice + actualPrice2)) /
          (marketingPrice + marketingPrice2)) *
        100
      );
    }

    return null;
  }

  get forSale(): boolean {
    return (
      (this.domain?.availableForPurchase || this.bundleAvailable) &&
      !this.isInCart &&
      !this.apiError
    );
  }

  get bundlePercentageDiscountLabel() {
    if (!this.bundle) {
      return null;
    }

    const discount = this.computePercentageDiscountForDomain(
      this.bundle.mainDomain,
      this.bundle.discountedDomain
    );
    return discount && discount > 0 ? Math.round(discount) + '%' : null;
  }

  get percentageDiscountLabel() {
    const discount = this.domain
      ? this.computePercentageDiscountForDomain(this.domain)
      : null;

    return discount && discount > 0 ? '-' + Math.round(discount) + '%' : null;
  }

  buy() {
    if (!this.forSale) {
      return;
    }
    (this.$refs.ctas as any)?.buy();
  }
}
</script>

<style lang="scss" scoped>
.cmp-domain-result-item {
  transition: all $element-hover-animation-speed $element-hover-animation-type;
  border: none;
  box-shadow: $ws-box-shadow7;
  border-radius: $domain-result-item-border-radius;

  // side borders
  .q-td {
    @include ws-typo-paragraph;
    height: 86px;
    padding-top: map-get($space-md, y);
    padding-bottom: map-get($space-md, y);
    border-color: $ws-cary-light;
  }

  &.ws-dark {
    &.q-tr {
      background: transparent;
    }

    &:not(.ws-highlighted)::v-deep {
      .ws-text-primary {
        color: $ws-white-00;
      }
      .ws-text-secondary {
        color: $ws-navy-60;
      }
      & > * {
        color: $ws-white-00;
      }
    }

    .q-td,
    &.mobile {
      border: none;
      background: $domain-result-item-background;
    }
  }

  &:not(.ws-dark) {
    .q-td,
    &.mobile {
      background: $domain-result-item-suggestion-bg;
    }

    &.error {
      &.q-tr {
        background: transparent;
        .q-td {
          background: #ffebe8;
        }
      }

      &.mobile {
        background: #ffebe8;
      }
    }
  }

  &.occupied {
    .q-td,
    &.mobile,
    &.mobile ::v-deep .domain-name {
      color: $ws-base110;
      ::v-deep .my-domain {
        color: $ws-text-color-dark;
      }
    }
  }

  &.ws-highlighted {
    &.q-tr {
      background: transparent;
      .q-td {
        color: $domain-result-item-title-color;
        background: $domain-result-item-highlighted-bg;
      }

      ::v-deep {
        & > *,
        .current-price {
          color: $domain-result-item-title-color;
        }

        & > *,
        .old-price, .period, .ws-tooltip, .prolong-price {
          color: $ws-base120;
        }

        & > *, .q-btn.ws-button.q-btn--flat {
          color: $button-primary-text;
        }
      }
    }

    &.mobile {
      color: $domain-result-item-title-color;
      background: $domain-result-item-highlighted-bg;
    }
  }

  &.for-sale {
    cursor: pointer;
  }

  &.is-loading {
    cursor: not-allowed;

    .q-td,
    &.mobile {
      ::v-deep {
        & > *,
        .prolong-price {
          opacity: 0.4;
        }
      }
    }
  }

  &.mobile {
    width: 100%;
    display: flex;
    align-items: center;
    padding: map-get($space-md, x);
    margin-bottom: map-get($space-sm, y);
    border: $ws-border-thick;
    border-radius: $ws-border-radius;

    &:last-of-type {
      margin-bottom: 0;
    }

    .domain-info-col {
      flex: 1 1 auto;
      min-width: 0;

      ::v-deep {
        > div,
        > span {
          display: block;
          width: 100%;
          text-align: left;
        }

        .domain-name {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-all;
        }
      }

      .prolong-price {
        color: $ws-base110;
      }
    }

    .item-ctas {
      flex: 0 1 auto;
      max-width: 60%;
    }
  }
}
</style>
